import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Popover,
  Skeleton,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  styled,
  tableCellClasses,
} from "@mui/material";
import TableCell from "@mui/material/TableCell";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTheme } from "@mui/styles";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import FilterModal from "../filterModal";
import DeleteConfirmationModal from "../deleteConfirmationModal";
import CustomPagination from "../pagination";
import TableHeader from "../table-header";
import CatalogViewModal from "./CatalogViewModal";
import EditIcon from "../../globalStyles/icons/editIcon";
import DeleteIcon from "../../globalStyles/icons/deleteIcon";
import ViewIcon from "../../globalStyles/icons/vieewIcon";
import { catalogService } from "../../utils/services/catalogService";
import { checkIfFiltered } from "../../utils/utility";
import AppContext from "../context/context";
import CustomTypography from "../../globalStyles/fonts";
import { isMobile } from "../../globalStyles/mobile";
import { formatDate } from "../../utils/formatDate";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "var(--table-header-bg)",
    border: "1px solid var(--border-color)",
    color: "var(--text-primary)",
    padding: "6px",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
    padding: "6px",
    color: "var(--text-primary)",
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "var(--row-bg-odd)",
    border: "1px solid var(--border-color)",
  },
  "&:nth-of-type(even)": {
    backgroundColor: "var(--row-bg-even)",
    border: "1px solid var(--border-color)",
  },
  "&:last-child td, &:last-child th": { border: 0 },
}));

const CatalogActionCell = React.memo(
  ({ catalog, onView, onEdit, onDelete }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleAction = (action) => {
      setAnchorEl(null);
      if (action === "view") onView(catalog);
      else if (action === "edit") onEdit(catalog);
      else if (action === "delete") onDelete(catalog);
    };

    return (
      <Box>
        <IconButton
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{ color: open ? "primary.main" : "inherit" }}
        >
          <MoreVertIcon />
        </IconButton>
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: isMobile() ? "bottom" : "top",
            horizontal: isMobile() ? "center" : "left",
          }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          disableRestoreFocus
        >
          <Box sx={{ display: "flex", flexDirection: "column", p: 1 }}>
            {[
              { action: "view", label: "View", Icon: ViewIcon },
              { action: "edit", label: "Edit", Icon: EditIcon },
              { action: "delete", label: "Delete", Icon: DeleteIcon },
            ].map(({ action, label, Icon }) => (
              <Button
                key={action}
                onClick={() => handleAction(action)}
                sx={{ textTransform: "none", justifyContent: "flex-start" }}
              >
                <Box display="flex" gap={1} alignItems="center">
                  <Icon />
                  <Typography
                    fontFamily="Poppins"
                    fontSize="16px"
                    color="var(--text-tertiary)"
                    fontWeight={500}
                  >
                    {label}
                  </Typography>
                </Box>
              </Button>
            ))}
          </Box>
        </Popover>
      </Box>
    );
  },
);

const CatalogList = ({
  data,
  onEditData,
  onDeleteData,
  dataCount,
  page,
  rowsPerPage,
  setPage,
  setRowsPerPage,
  filterData,
  onChangeFilter,
  client_id,
  loading,
  setLoading,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const primary_color = theme.palette?.primary?.main || "#D13438";
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [anchorElFilter, setAnchorElFilter] = useState(null);
  const [selectedFilterData, setSelectedFilterData] = useState({
    key: "",
    name: "",
    filter: [],
    sort: 0,
    id: "_id",
    displayKey: "name",
    fetchApi: () => {},
    loadMoreButton: false,
    options: [],
    searchKey: "",
  });
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [selectedCatalog, setSelectedCatalog] = useState(null);
  const [openViewModal, setOpenViewModal] = useState(false);

  const handleOpenFilter = useCallback(
    (event, filterKey) => {
      setAnchorElFilter(event.currentTarget);
      setOpenFilterModal(true);
      const col = filterData.columns.find((x) => x.key === filterKey);
      setSelectedFilterData(col);
    },
    [filterData.columns],
  );

  const handleSaveFilter = useCallback(
    (selectedOptions, filterKey, sortOrder, searchKey) => {
      onChangeFilter({
        key: filterKey,
        filter: selectedOptions,
        sort: sortOrder,
        searchKey,
        sort_key: filterData.columns.find((x) => x.key === filterKey)?.sort_key,
      });
      setAnchorElFilter(null);
      setOpenFilterModal(false);
    },
    [filterData.columns, onChangeFilter],
  );

  const confirmDelete = useCallback(
    async (e) => {
      e.preventDefault();
      try {
        setLoading(true);
        await catalogService.delete(selectedCatalog._id);
        toast.success("Catalog deleted successfully!");
        onDeleteData && onDeleteData();
      } catch (error) {
        console.error("Delete failed:", error);
        toast.error("Failed to delete catalog.");
      } finally {
        setLoading(false);
        setOpenConfirmation(false);
      }
    },
    [selectedCatalog, onDeleteData],
  );

  const columns = useMemo(
    () => [
      {
        key: "uid",
        title: "Catalog ID",
        render: (row) => (
          <Typography
            fontSize="12px"
            color="var(--text-secondary)"
            fontFamily="poppins"
          >
            {row.uid}
          </Typography>
        ),
      },
      {
        key: "name",
        title: "Name",
        filterKey: "name",
        handleOpenFilter,
        render: (row) => (
          <Typography
            fontSize="12px"
            color="var(--text-secondary)"
            fontFamily="poppins"
            fontWeight={500}
          >
            {row.name}
          </Typography>
        ),
      },
      {
        key: "type",
        title: "Type",
        filterKey: "type",
        handleOpenFilter,
        render: (row) => (
          <Typography
            fontSize="12px"
            color="var(--text-secondary)"
            fontFamily="poppins"
          >
            {row.type?.name || row.type || "—"}
          </Typography>
        ),
      },
      ...(client_id
        ? []
        : [
            {
              key: "client_id",
              title: "Client",
              filterKey: "client_id",
              handleOpenFilter,
              render: (row) => (
                <Typography
                  fontSize="12px"
                  color="var(--text-secondary)"
                  fontFamily="poppins"
                >
                  {row.client_id?.business_name || row.client_id?.name || "—"}
                </Typography>
              ),
            },
          ]),
      // {
      //   key: "products",
      //   title: "Products",
      //   render: (row) => (
      //     <Typography
      //       fontSize="12px"
      //       color="var(--text-secondary)"
      //       fontFamily="poppins"
      //     >
      //       {row.products?.length ?? 0}
      //     </Typography>
      //   ),
      // },
      {
        key: "valid_from",
        title: "Valid From",
        filterKey: "valid_from",
        handleOpenFilter,
        render: (row) => (
          <Typography
            fontSize="12px"
            color="var(--text-secondary)"
            fontFamily="poppins"
          >
            {formatDate(row.valid_from, "—")}
          </Typography>
        ),
      },
      {
        key: "valid_till",
        title: "Valid Till",
        filterKey: "valid_till",
        handleOpenFilter,
        render: (row) => (
          <Typography
            fontSize="12px"
            color="var(--text-secondary)"
            fontFamily="poppins"
          >
            {formatDate(row.valid_till, "—")}
          </Typography>
        ),
      },
      {
        key: "print_date",
        title: "Print Date",
        render: (row) => (
          <Typography
            fontSize="12px"
            color="var(--text-secondary)"
            fontFamily="poppins"
          >
            {formatDate(row.print_date, "—")}
          </Typography>
        ),
      },
      {
        key: "action",
        title: "",
        width: "3%",
        render: (row) => (
          <CatalogActionCell
            catalog={row}
            onView={(c) => {
              setSelectedCatalog(c);
              setOpenViewModal(true);
            }}
            onEdit={(c) => {
              localStorage.setItem(
                "catalog-filter-data",
                JSON.stringify({
                  filters: filterData.columns
                    .filter((x) => x.filter?.length > 0)
                    .map((x) => ({ key: x.key, values: x.filter })),
                  limit: filterData.limit,
                }),
              );
              navigate(`/manage-catalog/${c._id}`);
            }}
            onDelete={(c) => {
              setSelectedCatalog(c);
              setOpenConfirmation(true);
            }}
          />
        ),
      },
    ],
    [handleOpenFilter, client_id, navigate],
  );

  const NoDataImage = () => (
    <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
      <img
        src="no_data_product.png"
        alt="No data available"
        style={{ objectFit: "cover", height: "300px" }}
      />
    </Box>
  );

  return (
    <Box sx={{ width: "100%" }}>
      <TableContainer
        sx={{
          maxHeight: "calc(100vh - 200px)",
          overflowY: "auto",
          overflowX: "auto",
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableHeader
                  key={col.key}
                  title={col.title}
                  filterData={filterData}
                  filterKey={col.filterKey}
                  handleOpenFilter={col.handleOpenFilter}
                />
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              [...Array(10)].map((_, rowIndex) => (
                <TableRow key={rowIndex}>
                  {[...Array(columns.length)].map((_, colIndex) => (
                    <TableCell key={colIndex} sx={{ p: 2 }}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : data && data.length > 0 ? (
              data.map((row, idx) => (
                <StyledTableRow key={row._id || idx}>
                  {columns.map((col) => (
                    <StyledTableCell
                      key={col.key}
                      style={col.width ? { width: col.width } : {}}
                    >
                      {col.render ? col.render(row) : (row[col.key] ?? "—")}
                    </StyledTableCell>
                  ))}
                </StyledTableRow>
              ))
            ) : (
              <TableRow>
                <StyledTableCell colSpan={columns.length} align="center">
                  <NoDataImage />
                </StyledTableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {data && data.length > 0 && (
        <CustomPagination
          dataCount={dataCount}
          rowsPerPage={rowsPerPage}
          page={page}
          setPage={setPage}
          setRowsPerPage={(val) => {
            setRowsPerPage(val);
            setPage(0);
          }}
        />
      )}

      {/* Filter Modal */}
      <Popover
        open={Boolean(anchorElFilter)}
        anchorEl={anchorElFilter}
        onClose={() => {
          setAnchorElFilter(null);
          setOpenFilterModal(false);
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        disableScrollLock={true}
      >
        {openFilterModal && selectedFilterData && (
          <FilterModal
            open={openFilterModal}
            onClose={() => {
              setAnchorElFilter(null);
              setOpenFilterModal(false);
            }}
            filterKey={selectedFilterData.key}
            options={selectedFilterData.options || []}
            selectedValues={selectedFilterData.filter}
            currentSortOrder={selectedFilterData.sort}
            onSave={handleSaveFilter}
            hasMore={selectedFilterData.loadMoreButton}
            displayKey={selectedFilterData.displayKey}
            id={selectedFilterData.id}
            fetchApi={selectedFilterData.fetchApi}
            searchKey={selectedFilterData.searchKey}
            type={selectedFilterData.type}
            placeholderKey={
              selectedFilterData.placeholderKey || selectedFilterData.name
            }
          />
        )}
      </Popover>

      {/* Delete Confirmation */}
      <DeleteConfirmationModal
        open={openConfirmation}
        handleClose={() => setOpenConfirmation(false)}
        handleDelete={confirmDelete}
        title={`Delete Catalog: ${selectedCatalog?.uid} (${selectedCatalog?.name || ""})`}
      />

      {/* View Modal */}
      {openViewModal && selectedCatalog && (
        <CatalogViewModal
          open={openViewModal}
          catalog={selectedCatalog}
          onClose={() => setOpenViewModal(false)}
          client_id={client_id}
        />
      )}
    </Box>
  );
};

export default React.memo(CatalogList);
